/**
 * @tt/shell-3d — materials & palette
 *
 * The 3-step toon gradientMap is the single highest-leverage trick from the
 * spike ("what sells 'stylized' over 'programmer art'"). The DataTexture is
 * built once per step-count and shared by every material in the app.
 *
 * Palette tokens come from the visual bible (docs/planning/
 * Total-Redesign-Spec.md §1f). For 3D use, hexes should be re-checked
 * through the ACES curve — see acesAdjust().
 */
import * as THREE from 'three';

/** Canonical bible palette tokens (§1f). */
export const PALETTE = {
  soot: 0x0a0604,
  night: 0x0d0a14,
  nightBlue: 0x16203a,
  ink: 0x1d1208,
  inkSoft: 0x2a1c0e,
  woodDark: 0x3a2410,
  woodMid: 0x5b3a1f,
  woodLight: 0x8a5a18,
  brass: 0xb09a72,
  parchment: 0xf8edd6,
  parchmentDim: 0xd4c2a0,
  emberLow: 0xd98b4a,
  emberMid: 0xff8c42,
  emberHigh: 0xffeb6c,
  gold: 0xffd770,
  blood: 0xc94a2a,
  moss: 0x6abf3f,
  mist: 0xb6c8d4,
  violet: 0x9a4be8,
  cyanPulse: 0x6dd3c4,
} as const;

// One cached gradient texture per step count. 3 steps ([70,160,255]) is the
// spike-proven default; 2 reads flatter/grittier, 4+ approaches smooth.
const gradientCache = new Map<number, THREE.DataTexture>();

/** Shared toon gradient map (lazy-built, module-cached). */
export function getToonGradientMap(steps = 3): THREE.DataTexture {
  let tex = gradientCache.get(steps);
  if (tex) return tex;
  const data = new Uint8Array(steps);
  for (let i = 0; i < steps; i++) {
    // Spike's 3-step ramp generalized: dark floor 70, full-bright 255.
    data[i] = Math.round(70 + (185 * i) / Math.max(steps - 1, 1));
  }
  tex = new THREE.DataTexture(data, steps, 1, THREE.RedFormat);
  tex.minFilter = tex.magFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  gradientCache.set(steps, tex);
  return tex;
}

export interface ToonMaterialOptions {
  /** Gradient step count (shared texture per count). Default 3. */
  steps?: number;
  emissive?: number;
  emissiveIntensity?: number;
  transparent?: boolean;
  opacity?: number;
  /**
   * Hard-facet the surface (per-face normals). The single cheapest way to
   * read "chunky low-poly" — use it on angular parts (boxes, cylinders,
   * cones); leave it off for heads/orbs you want to read as smooth. Default
   * false.
   */
  flatShading?: boolean;
}

/**
 * MeshToonMaterial in a flat color with the shared gradient map.
 * Callers own disposal of the returned material; the gradient map is
 * module-shared and must NOT be disposed by callers.
 */
export function toonMaterial(
  colorHex: number,
  opts: ToonMaterialOptions = {},
): THREE.MeshToonMaterial {
  const mat = new THREE.MeshToonMaterial({
    color: acesPrecompEnabled ? acesAdjust(colorHex) : colorHex,
    gradientMap: getToonGradientMap(opts.steps ?? 3),
  });
  if (opts.emissive !== undefined) {
    mat.emissive.setHex(acesPrecompEnabled ? acesAdjust(opts.emissive) : opts.emissive);
  }
  if (opts.emissiveIntensity !== undefined) mat.emissiveIntensity = opts.emissiveIntensity;
  if (opts.transparent !== undefined) mat.transparent = opts.transparent;
  if (opts.opacity !== undefined) mat.opacity = opts.opacity;
  // `flatShading` is a generic material flag the renderer honors on toon
  // materials (faceted normals), but @types/three omits it from
  // MeshToonMaterial — assign through a narrow cast.
  if (opts.flatShading) (mat as unknown as { flatShading: boolean }).flatShading = true;
  return mat;
}

const _c = new THREE.Color();
const _c2 = new THREE.Color();

/**
 * Derive a cohesive accent hex from a base color by nudging it in HSL.
 * Defaults brighten + slightly warm-shift the hue — perfect for the
 * trim/emissive pips on a slot-colored figure so it reads as one creature,
 * not two random colors.
 */
export function deriveAccent(
  baseHex: number,
  opts: { lightness?: number; saturation?: number; hueShift?: number } = {},
): number {
  const { lightness = 0.16, saturation = 0.05, hueShift = 0.02 } = opts;
  _c.setHex(baseHex);
  const hsl = { h: 0, s: 0, l: 0 };
  _c.getHSL(hsl);
  _c.setHSL(
    (hsl.h + hueShift + 1) % 1,
    Math.min(1, Math.max(0, hsl.s + saturation)),
    Math.min(1, Math.max(0, hsl.l + lightness)),
  );
  return _c.getHex();
}

/**
 * Darken a hex by a 0..1 amount (toward black in linear-ish space). Handy
 * for prop bottoms, rims, and shadowed undersides.
 */
export function shade(baseHex: number, amount = 0.3): number {
  _c.setHex(baseHex);
  _c2.setRGB(0, 0, 0);
  return _c.lerp(_c2, Math.min(1, Math.max(0, amount))).getHex();
}

/** When false, toonMaterial() base/emissive colors skip acesAdjust() (a
 *  game can flip this for color-critical scenes). Toggle via
 *  {@link setAcesPrecompensation}. Default true. */
let acesPrecompEnabled = true;

/**
 * Globally enable/disable the {@link acesAdjust} pre-compensation that
 * `toonMaterial` applies to its base + emissive colors. Color-critical games
 * (e.g. ones whose gameplay reads on exact authored hues) can call
 * `setAcesPrecompensation(false)` once at boot — then authored hexes are fed
 * straight to the materials. Pair with a softer/disabled tonemap and/or lower
 * exposure on the renderer for full fidelity. Default true.
 */
export function setAcesPrecompensation(on: boolean): void {
  acesPrecompEnabled = on;
}

/** Whether toonMaterial currently pre-compensates colors for ACES. */
export function acesPrecompensationEnabled(): boolean {
  return acesPrecompEnabled;
}

/**
 * Palette pre-compensation for the ACES filmic tone curve.
 *
 * The bible's hexes are authored for 2D (sRGB, no tonemap). Under
 * `ACESFilmicToneMapping` at the default exposure (renderer.ts) the rendered color
 * shifts: ACES desaturates saturated mids and rolls highlights off toward
 * warm/white, so vivid gameplay colors come out muddy and harder to tell
 * apart. This boosts saturation (and nudges value) on the INPUT hex so that
 * AFTER the curve the on-screen result reads close to the intended hue — i.e.
 * the six hexagon-heat tile colors stay clearly distinguishable.
 *
 * It is a cheap analytic pre-compensation, not a true curve inverse: saturate
 * proportionally to how saturated the color already is (so near-greys are left
 * alone), with a small value lift to counter the highlight roll-off's tendency
 * to crush mid-bright saturated tones.
 */
export function acesAdjust(hex: number): number {
  _c.setHex(hex);
  const hsl = { h: 0, s: 0, l: 0 };
  _c.getHSL(hsl);
  // ACES desaturates more as saturation/brightness climb; pre-boost saturation
  // proportionally (greys ~untouched, vivid colors pushed toward full sat).
  const satBoost = 0.28 * hsl.s;
  const s = Math.min(1, hsl.s + satBoost);
  // Slight value lift on already-bright sat colors to survive the highlight
  // roll-off; darks are left alone so shadows don't wash out.
  const lLift = 0.06 * hsl.s * Math.max(0, hsl.l - 0.35);
  const l = Math.min(1, hsl.l + lLift);
  _c.setHSL(hsl.h, s, l);
  return _c.getHex();
}
